import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./PaymentBatches.css";
import "./BatchDetails.css";

const getTodayStr = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDateMonthDayYear = (dateInput?: string | Date | null): string => {
  if (!dateInput) return "-";

  if (typeof dateInput === "string") {
    const match = dateInput.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = parseInt(match[1], 10);
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${months[monthIdx]} ${day}, ${year}`;
      }
    }
  }

  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatPayoutStatementNote = (endDateStr?: string | null) => {
  if (!endDateStr) {
    const today = new Date();
    const formatted = today.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${formatted} Payout Statement`;
  }

  const datePart = String(endDateStr).split("T")[0];
  const parts = datePart.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    if (month >= 0 && month < 12) {
      return `${months[month]} ${day}, ${year} Payout Statement`;
    }
  }

  const d = new Date(endDateStr);
  const formatted = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${formatted} Payout Statement`;
};

interface RowInput {
  checkNumber: string;
  checkDate: string;
}

const BatchDetails: React.FC = () => {
  const { batch_id } = useParams<{ batch_id: string }>();

  const [batch, setBatch] = useState<any | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Check inputs per release: release.id -> { checkNumber, checkDate }
  const [rowInputs, setRowInputs] = useState<Record<number, RowInput>>({});
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [savingRows, setSavingRows] = useState<Record<number, boolean>>({});

  // Feedback message
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Status banner persists until user dismisses it manually (via ✕ button or page refresh)

  const {
    data: batchData,
    fetchData: fetchBatchDetails,
    isLoading,
    hasFetched,
    errorContent,
  } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/payments/batches/${batch_id}`,
    method: "GET",
  });

  useEffect(() => {
    if (batch_id) {
      fetchBatchDetails();
    }
  }, [batch_id, fetchBatchDetails]);

  useEffect(() => {
    if (batchData && !Array.isArray(batchData) && batchData.id) {
      setBatch(batchData);
    }
  }, [batchData]);

  // Initialize and update input fields when batch releases are loaded or refreshed
  useEffect(() => {
    if (batch?.store_releases && Array.isArray(batch.store_releases)) {
      const today = getTodayStr();
      setRowInputs((prev) => {
        const next = { ...prev };
        batch.store_releases.forEach((r: any) => {
          next[r.id] = {
            checkNumber: r.check_number || next[r.id]?.checkNumber || "",
            checkDate: r.check_issued_date
              ? String(r.check_issued_date).split("T")[0]
              : next[r.id]?.checkDate || today,
          };
        });
        return next;
      });
    }
  }, [batch]);

  // Trigger file selection for CSV/XLSX upload
  const handleTriggerUploadCsv = () => {
    if (isDownloading || isUploadingCsv) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle selected file with frontend validation
  const handleCsvFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name || "";
    const lowerName = fileName.toLowerCase();
    const isAllowedExt = lowerName.endsWith(".csv") || lowerName.endsWith(".xlsx");
    if (!isAllowedExt) {
      setStatusMessage({
        type: "error",
        text: "Invalid file type. Only CSV (.csv) and Excel (.xlsx) files are allowed.",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size === 0) {
      setStatusMessage({
        type: "error",
        text: "The selected file is empty.",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setStatusMessage({
        type: "error",
        text: "File size exceeds the limit of 5MB.",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    await uploadCsvFile(file);
  };

  // Upload CSV to backend API
  const uploadCsvFile = async (file: File) => {
    if (!batch?.id || isDownloading) return;
    setIsUploadingCsv(true);
    setStatusMessage(null);

    const formData = new FormData();
    formData.append("file", file);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/payments/batches/${batch.id}/upload-csv`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok && result?.success) {
        setStatusMessage({
          type: "success",
          text: result.message || "Payment batch spreadsheet imported successfully.",
        });
        fetchBatchDetails();
      } else {
        const errorDetail =
          result?.message?.replace(/^Error in \w+:\s*/i, "") ||
          (Array.isArray(result?.errors) && result.errors.length > 0
            ? result.errors.join("; ")
            : "Failed to upload file.");

        setStatusMessage({
          type: "error",
          text: `Import failed: ${errorDetail}`,
        });
      }
    } catch (err: any) {
      console.error("Upload spreadsheet error:", err);
      setStatusMessage({
        type: "error",
        text: `Import cancelled: ${err?.message || "An unexpected error occurred while uploading."}`,
      });
    } finally {
      setIsUploadingCsv(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Status mapping
  const totalStores = batch?.total_stores || batch?.store_releases?.length || 0;
  const releases = batch?.store_releases || [];
  const completedCount = releases.filter(
    (r: any) => r.status === "CHECK_ISSUED" || r.status === "COMPLETED"
  ).length;

  const isBatchComplete =
    batch?.status === "COMPLETED" ||
    (totalStores > 0 && completedCount === totalStores);

  let statusLabel = batch?.status || "Pending";
  let statusBadgeClass = "batch-details-badge--pending";

  if (isBatchComplete) {
    statusLabel = "Complete";
    statusBadgeClass = "batch-details-badge--complete";
  } else if (batch?.status === "CANCELLED") {
    statusLabel = "Cancelled";
    statusBadgeClass = "batch-details-badge--cancelled";
  } else if (
    batch?.status === "PARTIALLY_PROCESSED" ||
    (completedCount > 0 && completedCount < totalStores)
  ) {
    statusLabel = `${completedCount} of ${totalStores} issued`;
    statusBadgeClass = "batch-details-badge--partial";
  }

  const createdDate = formatDateMonthDayYear(batch?.createdAt);
  const creatorEmail = batch?.creator?.email || "admin@justforkix.com";

  const totalAmountNum =
    batch?.total_amount != null
      ? parseFloat(batch.total_amount)
      : releases.reduce(
          (sum: number, r: any) => sum + (parseFloat(r.amount_release) || 0),
          0
        );
  const totalAmountStr = `$${totalAmountNum.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  // Input change handler
  const handleInputChange = (
    releaseId: number,
    field: "checkNumber" | "checkDate",
    value: string
  ) => {
    setRowInputs((prev) => ({
      ...prev,
      [releaseId]: {
        checkNumber: prev[releaseId]?.checkNumber || "",
        checkDate: prev[releaseId]?.checkDate || getTodayStr(),
        [field]: value,
      },
    }));

    if (field === "checkNumber" && rowErrors[releaseId]) {
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[releaseId];
        return next;
      });
    }
  };

  // Save a single store payment
  const handleSaveSingle = async (releaseId: number) => {
    const input = rowInputs[releaseId];
    const checkNo = input?.checkNumber?.trim();
    const checkDate = input?.checkDate || getTodayStr();

    if (!checkNo) {
      setRowErrors((prev) => ({ ...prev, [releaseId]: "Required" }));
      return;
    }

    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[releaseId];
      return next;
    });

    setSavingRows((prev) => ({ ...prev, [releaseId]: true }));
    setStatusMessage(null);

    const release = releases.find((r: any) => r.id === releaseId);
    const payoutNote = formatPayoutStatementNote(release?.end_date);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/payments/batches/${batch_id}/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            store_payments: [
              {
                store_payment_release_id: releaseId,
                check_number: checkNo,
                check_issued_date: checkDate,
                note: payoutNote,
              },
            ],
          }),
        }
      );

      const result = await res.json();
      if (res.ok && result.success) {
        setStatusMessage({
          type: "success",
          text: result.message || "Payment saved successfully.",
        });
        await fetchBatchDetails();
      } else {
        const errMsg =
          result?.message?.replace(/^Error in \w+:\s*/i, "") ||
          "Failed to save payment.";
        setStatusMessage({
          type: "error",
          text: errMsg,
        });
      }
    } catch (err: any) {
      console.error("Payment save error:", err);
      setStatusMessage({
        type: "error",
        text: err?.message || "An error occurred while saving payment.",
      });
    } finally {
      setSavingRows((prev) => ({ ...prev, [releaseId]: false }));
    }
  };

  // Download Excel
  const handleDownload = async () => {
    if (!batch?.id || isDownloading || isUploadingCsv) return;
    setIsDownloading(true);
    const startTime = Date.now();
    try {
      const token = localStorage.getItem("token");
      const url = `${import.meta.env.VITE_API_URL}/payments/batches/download?batch_id=${batch.id}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download spreadsheet");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.position = "fixed";
      a.style.left = "-9999px";
      a.style.opacity = "0";
      a.href = downloadUrl;
      a.download = batch.file_name || `Payment_Release_Batch_${batch.id}.xlsx`;
      a.onclick = (e) => e.stopPropagation();
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        if (a.parentNode) {
          a.parentNode.removeChild(a);
        }
        window.URL.revokeObjectURL(downloadUrl);
      }, 200);
    } catch (err) {
      console.error("Download error:", err);
      setStatusMessage({
        type: "error",
        text: "Failed to download payment batch spreadsheet.",
      });
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 600 - elapsed);
      setTimeout(() => {
        setIsDownloading(false);
      }, remaining);
    }
  };

  return (
    <div className="account-section w-clearfix stores-section">
      <div className="div-block-28">
        <div className="batch-details-table-wrapper">
          {/* Breadcrumb Navigation */}
          <div className="batch-details-breadcrumbs">
            <Link to="/payment-batches" className="batch-details-breadcrumb-link">
              Payment Batches
            </Link>
            <span className="batch-details-breadcrumb-sep">/</span>
            <span className="batch-details-breadcrumb-current">
              Batch #{batch?.id || batch_id}
            </span>
          </div>

          {/* Loading Skeleton */}
          {isLoading && !hasFetched && !batch && (
            <div className="batches-skeleton-wrapper batch-details-skeleton-container">
              {[1, 2, 3].map((i) => (
                <div key={i} className="batch-details-skeleton-card">
                  <div className="batch-details-skeleton-line" />
                </div>
              ))}
            </div>
          )}

          {/* Batch Not Found / Error State */}
          {!isLoading && hasFetched && !batch && (
            <div className="batch-details-empty batch-details-empty--error">
              {errorContent || `Batch #${batch_id} not found.`}
            </div>
          )}

          {/* Batch Content (Only shown when batch exists) */}
          {batch && (
            <>
              {/* Top Header Card */}
              <div className="batch-details-header-card">
                {/* Top Section */}
                <div className="batch-details-header-top">
                  <div className="batch-details-meta">
                    <div className="batch-details-title-row">
                      <h1 className="batch-details-title">Batch #{batch.id}</h1>
                      <span className={`batch-details-badge ${statusBadgeClass}`}>
                        {statusLabel}
                      </span>
                    </div>
                    <p className="batch-details-subtitle">
                      Created {createdDate} by {creatorEmail} · All payments by check
                    </p>
                  </div>

                  <div className="batch-details-header-actions">
                    {isBatchComplete ? (
                      <div className="batch-details-locked-indicator">
                        <LockOutlinedIcon className="batch-details-lock-icon" />
                        <span>Uploads locked (batch complete)</span>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={`batch-details-upload-btn ${
                            isUploadingCsv ? "batch-details-upload-btn--loading" : ""
                          }`}
                          onClick={handleTriggerUploadCsv}
                          disabled={
                            isUploadingCsv ||
                            isDownloading ||
                            batch.status === "CANCELLED"
                          }
                        >
                          <span className="batch-details-btn-icon-slot">
                            {isUploadingCsv ? (
                              <span className="batch-details-btn-spinner" />
                            ) : (
                              <UploadFileIcon className="batch-details-upload-icon" />
                            )}
                          </span>
                          <span>Upload Spreadsheet (.xlsx)</span>
                        </button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          className="batch-details-file-input"
                          onChange={handleCsvFileChange}
                        />
                      </>
                    )}

                    <button
                      type="button"
                      className={`batch-details-download-btn ${
                        isDownloading ? "batch-details-download-btn--loading" : ""
                      }`}
                      onClick={handleDownload}
                      disabled={isDownloading || isUploadingCsv}
                    >
                      <span className="batch-details-btn-icon-slot">
                        {isDownloading ? (
                          <span className="batch-details-btn-spinner" />
                        ) : (
                          <DownloadIcon className="batch-details-download-icon" />
                        )}
                      </span>
                      <span>Download Spreadsheet (.xlsx)</span>
                    </button>
                  </div>
                </div>

                {/* Bottom Metrics Section */}
                <div className="batch-details-metrics-row">
                  <div className="batch-details-metric-col">
                    <span className="batch-details-metric-label">TOTAL AMOUNT</span>
                    <span className="batch-details-metric-value">{totalAmountStr}</span>
                  </div>
                  <div className="batch-details-metric-col">
                    <span className="batch-details-metric-label">STORES</span>
                    <span className="batch-details-metric-value">{totalStores}</span>
                  </div>
                  <div className="batch-details-metric-col">
                    <span className="batch-details-metric-label">CHECKS ISSUED</span>
                    <span className="batch-details-metric-value">
                      {completedCount} of {totalStores}
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating Toast Notification (Zero layout shift on page) */}
              {statusMessage && (
                <div
                  className={`batch-details-toast batch-details-toast--${statusMessage.type}`}
                  title={statusMessage.text}
                >
                  {statusMessage.type === "success" ? (
                    <CheckCircleOutlineIcon className="batch-details-toast-icon" />
                  ) : (
                    <ErrorOutlineIcon className="batch-details-toast-icon" />
                  )}
                  <span className="batch-details-toast-text">
                    {statusMessage.text}
                  </span>
                  <button
                    type="button"
                    className="batch-details-toast-dismiss"
                    onClick={() => setStatusMessage(null)}
                    aria-label="Close notification"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Table Card */}
              <div className="batch-details-table-card">
                <table className="batch-details-table">
                  <thead>
                    <tr>
                      <th className="batch-details-th batch-details-th--store">STORE</th>
                      <th className="batch-details-th batch-details-th--period">RELEASE PERIOD</th>
                      <th className="batch-details-th batch-details-th--checkno">CHECK #</th>
                      <th className="batch-details-th batch-details-th--date">ISSUE DATE</th>
                      <th className="batch-details-th batch-details-th--amount">AMOUNT</th>
                      <th className="batch-details-th batch-details-th--action">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releases.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="batch-details-empty-td">
                          No stores found in this batch.
                        </td>
                      </tr>
                    ) : (
                      releases.map((release: any) => {
                        const storeName =
                          release.store?.name || `Store #${release.store_id}`;
                        const startDateStr = formatDateMonthDayYear(
                          release.start_date
                        );
                        const endDateStr = formatDateMonthDayYear(
                          release.end_date
                        );
                        const periodStr = `${startDateStr} - ${endDateStr}`;
                        const amountStr =
                          release.amount_release != null
                            ? `$${parseFloat(release.amount_release).toFixed(2)}`
                            : "$0.00";

                        const isIssued =
                          release.status === "CHECK_ISSUED" ||
                          release.status === "COMPLETED" ||
                          (!!release.check_number && !!release.check_issued_date);
                        const isCancelled = release.status === "CANCELLED";
                        const checkNo = release.check_number || rowInputs[release.id]?.checkNumber || "";
                        const checkDate = release.check_issued_date
                          ? formatDateMonthDayYear(release.check_issued_date)
                          : (rowInputs[release.id]?.checkDate ? formatDateMonthDayYear(rowInputs[release.id]?.checkDate) : "");

                        const rowSaving = !!savingRows[release.id];

                        return (
                          <tr key={release.id} className="batch-details-tr">
                            {/* STORE */}
                            <td className="batch-details-td batch-details-td--store" data-label="Store">
                              <span className="batch-details-store-name">
                                {storeName}
                              </span>
                              <span className="batch-details-store-id">
                                #{release.store_id}
                              </span>
                            </td>

                            {/* RELEASE PERIOD */}
                            <td className="batch-details-td batch-details-td--period" data-label="Release Period">
                              {periodStr}
                            </td>

                            {/* CHECK # */}
                            <td className="batch-details-td batch-details-td--checkno" data-label="Check #">
                              {isIssued ? (
                                <span className="batch-details-cell-text">{checkNo || "—"}</span>
                              ) : (
                                <div className="batch-details-input-container">
                                  <input
                                    type="text"
                                    className={`batch-details-input batch-details-input--check ${
                                      rowErrors[release.id]
                                        ? "batch-details-input--error"
                                        : ""
                                    }`}
                                    placeholder="Check #"
                                    value={
                                      rowInputs[release.id]?.checkNumber ?? ""
                                    }
                                    onChange={(e) =>
                                      handleInputChange(
                                        release.id,
                                        "checkNumber",
                                        e.target.value
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !rowSaving) {
                                        e.preventDefault();
                                        handleSaveSingle(release.id);
                                      }
                                    }}
                                    disabled={rowSaving}
                                  />
                                  {rowErrors[release.id] && (
                                    <span className="batch-details-row-error">
                                      {rowErrors[release.id]}
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>

                            {/* ISSUE DATE */}
                            <td className="batch-details-td batch-details-td--date" data-label="Issue Date">
                              {isIssued ? (
                                <span className="batch-details-cell-text">{checkDate || "—"}</span>
                              ) : (
                                <div className="batch-details-input-container">
                                  <input
                                    type="date"
                                    className="batch-details-input batch-details-input--date"
                                    value={
                                      rowInputs[release.id]?.checkDate ??
                                      getTodayStr()
                                    }
                                    max={getTodayStr()}
                                    onChange={(e) =>
                                      handleInputChange(
                                        release.id,
                                        "checkDate",
                                        e.target.value
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !rowSaving) {
                                        e.preventDefault();
                                        handleSaveSingle(release.id);
                                      }
                                    }}
                                    disabled={rowSaving}
                                  />
                                </div>
                              )}
                            </td>

                            {/* AMOUNT */}
                            <td className="batch-details-td batch-details-td--amount" data-label="Amount">
                              {amountStr}
                            </td>

                            {/* ACTION / STATUS */}
                            <td className="batch-details-td batch-details-td--action" data-label="Action">
                              {isCancelled ? (
                                <span className="batch-details-status-badge batch-details-status-badge--cancelled">
                                  Cancelled
                                </span>
                              ) : isIssued ? (
                                <span className="batch-details-paid-badge">
                                  <CheckIcon className="batch-details-check-icon" /> Paid
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="batch-details-pay-btn"
                                  onClick={() => handleSaveSingle(release.id)}
                                  disabled={rowSaving}
                                >
                                  {rowSaving ? (
                                    <span className="batch-details-btn-spinner batch-details-btn-spinner--row" />
                                  ) : (
                                    "Save"
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchDetails;
